import { MaterialCommunityIcons } from "@expo/vector-icons";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import type { DiagnosisSource, HubDiagnosis } from "./types";

import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

// ── Color maps ──

const BORDER_COLOR: Record<string, string> = {
    section: colors.teal400,
    med: colors.pink400,
    agency: colors.gray300
};

const DOT_COLOR: Record<string, string> = {
    section: colors.teal400,
    med: colors.pink400,
    agency: colors.gray400
};

const CHIP_BG: Record<string, string> = {
    section: colors.teal50,
    med: colors.pink50,
    agency: colors.gray100
};

const CHIP_TEXT: Record<string, string> = {
    section: colors.teal600,
    med: colors.pink500,
    agency: colors.gray500
};

interface Props {
    diagnosis: HubDiagnosis;
    onMenuPress: (source: DiagnosisSource) => void;
    onClassificationPress: () => void;
    onNavigate: (source: DiagnosisSource) => void;
}

export function DiagnosisCard({
    diagnosis,
    onMenuPress,
    onClassificationPress,
    onNavigate
}: Props) {
    const { icdCode, description, classification, sources } = diagnosis;
    const isGrouped = sources.length > 1;
    const isPrincipal = classification === "principal";
    const primarySourceType = sources[0].type;

    return (
        <View style={[styles.card, isPrincipal && styles.cardPrincipal]}>
            {/* Top gradient accent for principal */}
            {isPrincipal && <View style={styles.principalAccent} />}

            <View
                style={[
                    styles.cardInner,
                    { borderLeftColor: BORDER_COLOR[primarySourceType] ?? colors.gray300 },
                    isPrincipal && styles.cardInnerPrincipal
                ]}
            >
                {/* Row 1: ICD Code + Classification badge */}
                <View style={styles.topRow}>
                    <View style={styles.icdBadge}>
                        <Text style={styles.icdText}>{icdCode}</Text>
                    </View>
                    <TouchableOpacity onPress={onClassificationPress} activeOpacity={0.7}>
                        <View
                            style={[
                                styles.classificationBadge,
                                isPrincipal
                                    ? styles.classificationPrincipal
                                    : styles.classificationOther
                            ]}
                        >
                            <Text
                                style={[
                                    styles.classificationText,
                                    isPrincipal
                                        ? styles.classificationTextPrincipal
                                        : styles.classificationTextOther
                                ]}
                            >
                                {classification.toUpperCase()}
                            </Text>
                        </View>
                    </TouchableOpacity>
                </View>

                {/* Row 2: Diagnosis name */}
                <Text style={styles.name} numberOfLines={2}>
                    {description}
                </Text>

                {/* Row 3: Source chip + menu (single-source only) */}
                {!isGrouped && (
                    <View style={styles.bottomRow}>
                        <SourceChip source={sources[0]} />
                        <TouchableOpacity
                            onPress={() => onMenuPress(sources[0])}
                            hitSlop={8}
                            style={styles.menuBtn}
                        >
                            <MaterialCommunityIcons
                                name="dots-vertical"
                                size={18}
                                color={colors.gray400}
                            />
                        </TouchableOpacity>
                    </View>
                )}
            </View>

            {/* Expansion area for grouped diagnoses */}
            {isGrouped && (
                <View style={styles.expandArea}>
                    <Text style={styles.expandLabel}>DOCUMENTED IN {sources.length} PLACES</Text>
                    {sources.map((source, i) => (
                        <SourceRow
                            key={i}
                            source={source}
                            onNavigate={() => onNavigate(source)}
                            onMenuPress={() => onMenuPress(source)}
                        />
                    ))}
                </View>
            )}
        </View>
    );
}

// ── Source Chip (for single-source cards) ──

function SourceChip({ source }: { source: DiagnosisSource }) {
    const label =
        source.type === "med"
            ? source.label
            : source.type === "agency"
              ? "Agency pre-visit"
              : source.label;

    return (
        <View style={[styles.chip, { backgroundColor: CHIP_BG[source.type] }]}>
            {source.type === "med" && (
                <MaterialCommunityIcons name="pill" size={12} color={CHIP_TEXT[source.type]} />
            )}
            {source.type === "agency" && (
                <MaterialCommunityIcons
                    name="diamond-outline"
                    size={11}
                    color={CHIP_TEXT[source.type]}
                />
            )}
            <Text style={[styles.chipText, { color: CHIP_TEXT[source.type] }]}>{label}</Text>
        </View>
    );
}

// ── Source Row (for multi-source expansion) ──

function SourceRow({
    source,
    onNavigate,
    onMenuPress
}: {
    source: DiagnosisSource;
    onNavigate: () => void;
    onMenuPress: () => void;
}) {
    const label =
        source.type === "section"
            ? `${source.label} section`
            : source.type === "med"
              ? `Medications (${source.label})`
              : source.label;

    return (
        <View style={styles.sourceRow}>
            <View style={styles.sourceRowLeft}>
                <View style={[styles.dot, { backgroundColor: DOT_COLOR[source.type] }]} />
                <Text style={styles.sourceRowText}>{label}</Text>
            </View>
            <View style={styles.sourceRowRight}>
                {(source.type === "section" || source.type === "med") && (
                    <TouchableOpacity
                        onPress={onNavigate}
                        activeOpacity={0.7}
                        style={styles.navigateLink}
                    >
                        <MaterialCommunityIcons
                            name="chevron-right"
                            size={14}
                            color={colors.teal600}
                        />
                        <Text style={styles.navigateText}>Navigate</Text>
                    </TouchableOpacity>
                )}
                <TouchableOpacity onPress={onMenuPress} hitSlop={8} style={styles.menuBtn}>
                    <MaterialCommunityIcons name="dots-vertical" size={18} color={colors.gray400} />
                </TouchableOpacity>
            </View>
        </View>
    );
}

// ── Styles ──

const styles = StyleSheet.create({
    card: {
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
        overflow: "hidden",
        ...shadows.sm
    },
    cardPrincipal: {
        borderColor: colors.teal200,
        ...shadows.md
    },
    principalAccent: {
        height: 3,
        backgroundColor: colors.teal400
    },
    cardInner: {
        padding: 14,
        paddingLeft: 18,
        borderLeftWidth: 4,
        borderLeftColor: colors.gray300,
        gap: 8
    },
    cardInnerPrincipal: {
        borderLeftWidth: 5,
        backgroundColor: "rgba(69, 210, 176, 0.03)"
    },

    // Top row
    topRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    icdBadge: {
        backgroundColor: colors.teal50,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4
    },
    icdText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.teal600,
        letterSpacing: 0.3
    },
    classificationBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 4
    },
    classificationPrincipal: {
        backgroundColor: colors.teal400
    },
    classificationOther: {
        backgroundColor: colors.gray100
    },
    classificationText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        letterSpacing: 0.6,
        textTransform: "uppercase"
    },
    classificationTextPrincipal: {
        color: "#FFFFFF"
    },
    classificationTextOther: {
        color: colors.gray500
    },

    // Name
    name: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        lineHeight: 20
    },

    // Bottom row (single-source)
    bottomRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },

    // Chip
    chip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 20
    },
    chipText: {
        fontSize: 11,
        fontFamily: fonts.semiBold
    },

    // Menu button
    menuBtn: {
        width: 28,
        height: 28,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center"
    },

    // Expansion area
    expandArea: {
        borderTopWidth: 1,
        borderTopColor: colors.border,
        backgroundColor: colors.gray50,
        padding: 12,
        paddingTop: 10,
        gap: 8
    },
    expandLabel: {
        fontSize: 10,
        fontFamily: fonts.bold,
        letterSpacing: 0.6,
        color: colors.gray400,
        marginBottom: 2
    },

    // Source row
    sourceRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: colors.surface,
        borderRadius: radius.xs,
        borderWidth: 1,
        borderColor: colors.border,
        paddingVertical: 10,
        paddingHorizontal: 12,
        ...shadows.xs
    },
    sourceRowLeft: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        flex: 1
    },
    dot: {
        width: 7,
        height: 7,
        borderRadius: 4
    },
    sourceRowText: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    sourceRowRight: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    navigateLink: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2
    },
    navigateText: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.teal600
    }
});
